import { safeDelay, SafePromise, SafeType } from '@benzinga/safe-await';
import { isUSDCryptoSymbol } from '@benzinga/crypto-manager';
import {
  QuotesManager,
  RankingDetail,
  TickerDetail,
  TickerDetailsResponse,
} from '@benzinga/quotes-manager';
import { normalizeWatchlistSymbol } from '@benzinga/watchlist-manager';
import { logger } from '@benzinga/logging';

export type RankingsRow = {
  rankings?: RankingDetail | null;
  symbol: string;
};

const DEFAULT_RETRY_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAY_MS = 250;

export const retrySafePromise = async <T>(
  request: () => SafePromise<T>,
  options?: {
    attempts?: number;
    delayMs?: number;
    onRetry?: (error: unknown, attempt: number) => void;
  },
): SafePromise<T> => {
  const attempts = options?.attempts ?? DEFAULT_RETRY_ATTEMPTS;
  const delayMs = options?.delayMs ?? DEFAULT_RETRY_DELAY_MS;
  let lastResult = await request();

  for (let attempt = 1; attempt < attempts && lastResult.err; attempt += 1) {
    options?.onRetry?.(lastResult.err, attempt);
    if (attempt > 1) {
      await safeDelay(delayMs);
    }
    lastResult = await request();
  }

  return lastResult;
};

export const getTickerDetailsWithRetry = (
  quoteManager: Pick<QuotesManager, 'getTickerDetails'>,
  symbols: string[],
): SafePromise<TickerDetailsResponse> => {
  if (symbols.length === 0) {
    return Promise.resolve({ ok: { result: [] } });
  }

  return retrySafePromise(() => quoteManager.getTickerDetails(symbols), {
    onRetry: (error, attempt) => {
      logger.error(
        attempt === 1
          ? 'watchlist ticker details failed; retrying'
          : 'watchlist ticker details failed; delayed retry',
        error,
      );
    },
  }).then(result => {
    if (result.err) {
      logger.error('watchlist ticker details failed after retries', result.err);
    }
    return result;
  });
};

export const getSymbolsMissingRankings = (symbols: string[], rows: RankingsRow[]): string[] => {
  const rankingsBySymbol = new Map(
    rows.map(row => [normalizeWatchlistSymbol(row.symbol), row.rankings ?? null]),
  );
  return symbols.filter(symbol => rankingsBySymbol.get(normalizeWatchlistSymbol(symbol)) == null);
};

export const mergeRankingsIntoRows = <T extends RankingsRow>(
  rows: T[],
  tickerDetails: SafeType<TickerDetailsResponse> | null | undefined,
): T[] => {
  if (!tickerDetails?.ok?.result?.length) {
    return rows;
  }

  const rankingsBySymbol = new Map<string, RankingDetail>();
  tickerDetails.ok.result.forEach((tickerDetail: TickerDetail) => {
    if (tickerDetail.rankings) {
      rankingsBySymbol.set(normalizeWatchlistSymbol(tickerDetail.symbol), tickerDetail.rankings);
    }
  });

  return rows.map(row => {
    if (row.rankings != null) {
      return row;
    }
    const rankings = rankingsBySymbol.get(normalizeWatchlistSymbol(row.symbol));
    return rankings ? { ...row, rankings } : row;
  });
};

export const isWatchlistFinancialsDegraded = (
  rows: RankingsRow[],
  expectedSymbolCount: number,
): boolean => {
  if (expectedSymbolCount > 0 && rows.length === 0) {
    return true;
  }
  if (rows.length === 0) {
    return false;
  }
  const hasEquitySymbol = rows.some(row => !isUSDCryptoSymbol(row.symbol));
  return hasEquitySymbol && rows.every(row => row.rankings == null);
};

export const rankingsFromTickerDetails = (
  tickerDetails: SafeType<TickerDetailsResponse> | null | undefined,
  symbol: string,
): RankingDetail | null => {
  const normalizedSymbol = normalizeWatchlistSymbol(symbol);
  return (
    tickerDetails?.ok?.result?.find(
      tickerDetail => normalizeWatchlistSymbol(tickerDetail.symbol) === normalizedSymbol,
    )?.rankings ?? null
  );
};
